import React, { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useSubscription } from './SubscriptionContext';
import { useUser } from './hooks/UserContext';
import PublicFooter from './components/lp/Footer';
import { handleLogout as performLogout } from './utils/logout';
import { useCookieConsent } from './hooks/useCookieConsent';

// --- START: Components for Public-Facing Pages ---
const PublicHeader = () => {
    const [isSupportOpen, setIsSupportOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const supportMenuRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const handleNavClick = (targetId) => {
        setIsMobileMenuOpen(false);
        if (location.pathname === '/') {
            const element = document.getElementById(targetId);
            if (element) {
                const headerOffset = 80;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
            }
        } else {
            navigate('/', { state: { targetId } });
        }
    };

    const handleContactClick = (e) => {
        e.preventDefault();
        setIsSupportOpen(false);
        setIsMobileMenuOpen(false);
        handleNavClick('contact');
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (supportMenuRef.current && !supportMenuRef.current.contains(event.target)) {
                setIsSupportOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    return (
        <header className="bg-white shadow-sm sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    <div className="flex-shrink-0">
                        <Link to="/">
                            <img className="h-8 w-auto" src="/assets/logo.png" alt="BillWave" />
                        </Link>
                    </div>

                    {/* Mobile Hamburger Button */}
                    <div className="flex md:hidden">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            type="button"
                            className="p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100 focus:outline-none"
                            aria-label="Toggle Navigation Menu"
                        >
                            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                    
                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-7"> 
                        <button onClick={() => handleNavClick('features')} className="text-gray-600 hover:text-blue-600 text-sm font-medium transition-colors">機能</button>
                        <button onClick={() => handleNavClick('pricing')} className="text-gray-600 hover:text-blue-600 text-sm font-medium transition-colors">料金</button>
                        
                        <div className="relative" ref={supportMenuRef}>
                            <button onClick={() => setIsSupportOpen(!isSupportOpen)} className="flex items-center gap-1 text-gray-600 hover:text-blue-600 text-sm font-medium transition-colors">
                                サポート <ChevronDown size={16} />
                            </button>
                            {isSupportOpen && (
                                <div className="absolute top-full mt-2 w-48 bg-white rounded-md shadow-lg border py-1 z-10">
                                    <Link to="/support/how-to-use" onClick={() => setIsSupportOpen(false)} className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50">使い方</Link>
                                    <Link to="/support/csv-format" onClick={() => setIsSupportOpen(false)} className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50">CSVフォーマット</Link>
                                    <a href="/#contact" onClick={handleContactClick} className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50">お問い合わせ</a>
                                </div>
                            )}
                        </div>

                        <Link to="/faq" className="text-gray-600 hover:text-blue-600 text-sm font-medium transition-colors">FAQ</Link>
                        <Link to="/login" className="text-gray-600 hover:text-blue-600 text-sm font-medium transition-colors">ログイン</Link>
                        
                        <Link to="/signup" className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap">
                            無料ではじめる
                        </Link>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden border-b border-gray-200 bg-white px-4 pt-2 pb-4 space-y-2">
                    <button 
                        onClick={() => handleNavClick('features')} 
                        className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100"
                    >
                        機能
                    </button>
                    <button 
                        onClick={() => handleNavClick('pricing')} 
                        className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100"
                    >
                        料金
                    </button>
                    <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">サポート</div>
                    <Link to="/support/how-to-use" className="block pl-6 pr-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100">使い方</Link>
                    <Link to="/support/csv-format" className="block pl-6 pr-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100">CSVフォーマット</Link>
                    <a href="/#contact" onClick={handleContactClick} className="block pl-6 pr-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100">お問い合わせ</a>
                    <Link to="/faq" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100">FAQ</Link>
                    <div className="pt-2 border-t border-gray-100 flex flex-col gap-2">
                        <Link to="/login" className="block w-full text-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                            ログイン
                        </Link>
                        <Link to="/signup" className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700">
                            無料ではじめる
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

const PublicPageLayout = () => (
    <div className="bg-gray-50 min-h-screen flex flex-col">
        <PublicHeader />
        <main className="flex-grow">
          <Outlet />
        </main>
        <PublicFooter />
    </div>
);

// --- START: Components for Logged-In Users ---
const Header = ({ showLogout, toggleSidebar, isSidebarOpen }) => {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState(null);

  useEffect(() => {
    if (showLogout) {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          setUserEmail(user.email);
        } else {
          setUserEmail(null);
          navigate('/login');
        }
      });
      return () => unsubscribe();
    }
  }, [showLogout, navigate]);

  const handleLogout = async () => {
    try {
      await performLogout();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <header className="bg-white px-4 py-2.5 flex justify-between items-center border-b border-gray-200 sticky top-0 z-30">
        <div className="flex items-center space-x-3">
            {/* Hamburger Button for Mobile */}
            <button
                onClick={toggleSidebar}
                className="lg:hidden p-1.5 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100 focus:outline-none"
                aria-label="Toggle menu"
            >
                {isSidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <Link to="/dashboard" className="flex items-center">
                <img src="/assets/logo.png" alt="billwave logo" className="h-9 sm:h-10" />
            </Link>
        </div>
      {showLogout && (
        <div className="flex items-center space-x-3">
          {userEmail && <p className="hidden sm:block text-xs sm:text-sm text-gray-700 truncate max-w-[180px] sm:max-w-xs">{userEmail}</p>}
          <button 
            onClick={handleLogout} 
            className="py-1.5 px-3 sm:py-2 sm:px-4 text-xs sm:text-sm cursor-pointer border border-gray-300 rounded-md bg-gray-50 hover:bg-gray-100 whitespace-nowrap"
          >
            ログアウト
          </button>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
    const footerLinkClassName = "text-gray-600 hover:text-blue-600 transition-colors text-sm";
    const { openSettingsModal } = useCookieConsent();

    return (
        <footer className="bg-[#f8faff] pt-12 pb-8 border-t border-gray-200 mt-auto text-left">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-10 gap-x-8">
                    {/* Logo & Tagline */}
                    <div className="lg:col-span-3">
                        <img src="/assets/logo.png" alt="Billwave" className="h-7 mb-3" />
                        <p className="text-xs text-gray-500">ー 請求業務に新しい波を ー</p>
                    </div>

                    {/* Links */}
                    <div className="lg:col-span-9 grid grid-cols-2 md:grid-cols-4 gap-8">
                        <div>
                            <h3 className="font-bold text-blue-600 mb-4 text-sm">サポート</h3>
                            <ul className="space-y-3">
                                <li><Link to="/app/support/how-to-use" className={footerLinkClassName}>使い方</Link></li>
                                <li><Link to="/app/support/csv-format" className={footerLinkClassName}>CSVフォーマット</Link></li>
                                <li><Link to="/app/support/faq" className={footerLinkClassName}>よくある質問</Link></li>
                                <li><Link to="/app/support/troubleshooting" className={footerLinkClassName}>エラー・トラブル</Link></li>
                                <li><Link to="/app/support/contact" className={footerLinkClassName}>お問い合わせ</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-bold text-blue-600 mb-4 text-sm">アカウント</h3>
                            <ul className="space-y-3">
                                <li><Link to="/subscription" className={footerLinkClassName}>ご契約内容</Link></li>
                                <li><Link to="/settings/billing-history" className={footerLinkClassName}>ご利用料金</Link></li>
                                <li><Link to="/settings/customer-info" className={footerLinkClassName}>お客様情報</Link></li>
                                <li><Link to="/settings/team-management" className={footerLinkClassName}>チーム管理</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-bold text-blue-600 mb-4 text-sm">法的情報</h3>
                            <ul className="space-y-3">
                                <li><Link to="/terms" className={footerLinkClassName}>利用規約</Link></li>
                                <li><a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className={footerLinkClassName}>個人情報保護方針</a></li>
                                <li><Link to="/cookie-policy" className={footerLinkClassName}>Cookieポリシー</Link></li>
                                <li>
                                  <button onClick={openSettingsModal} className={footerLinkClassName}>
                                    Cookie設定
                                  </button>
                                </li>
                                <li><a href="/tokushoho" target="_blank" rel="noopener noreferrer" className={footerLinkClassName}>特定商取引法に基づく表記</a></li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-bold text-blue-600 mb-4 text-sm">システム</h3>
                            <ul className="space-y-3">
                                <li><Link to="/announcements" className={footerLinkClassName}>お知らせ</Link></li>
                                <li><Link to="/app/release-notes" className={footerLinkClassName}>リリースノート</Link></li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Copyright */}
                <div className="mt-10 pt-6 border-t border-gray-200 text-xs text-gray-500">
                    <p>&copy; 2026 SIGNAL CORPORATION</p>
                </div>
            </div>
        </footer>
    );
};

const MainLayout = () => {
  const { plan, accountStatus, loading: isPlanLoading } = useSubscription();
  const { hasPermission, isAdmin, loading: isUserLoading } = useUser();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const customerFeatures = ['trial', 'lite', 'standard', 'business'];
  const teamManagementFeatures = ['trial', 'standard', 'business'];

  const isRestrictedStatus = accountStatus === 'canceling' || accountStatus === 'suspended_support_only';
  const allowedPaths = [
    '/billing-history',
    '/subscription',
    '/settings/billing-history',
    '/settings/customer-info',
    '/app/support/contact'
  ];

  // Close sidebar on route change on mobile
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  if (!isPlanLoading && !isUserLoading && isRestrictedStatus && !allowedPaths.includes(location.pathname)) {
      return <Navigate to="/subscription" replace />;
  }

  const h2Style = { fontSize: '15px', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginBottom: '8px' };
  const iconStyle = { marginRight: '8px' };
  const linkStyle = { display: 'flex', alignItems: 'center', textDecoration: 'none', color: '#333', fontSize: '14px', marginBottom: '8px', padding: '4px 8px', borderRadius: '4px' };
  const subIconStyle = { marginRight: '8px', fontSize: '18px' };

  const loadingScreen = (
      <div className="flex flex-col min-h-screen">
          <Header showLogout={true} />
          <div className="flex flex-grow">
              <nav className="w-60 flex-shrink-0 border-r border-gray-200 p-5 bg-white"><p>読み込み中...</p></nav>
              <main className="flex-1 p-4 sm:p-6 overflow-auto bg-gray-50"><Outlet /></main>
          </div>
          <Footer />
      </div>
  );

  if (isPlanLoading || isUserLoading) {
      return loadingScreen;
  }

  const navContent = (
    <ul className="space-y-4">
      {!isRestrictedStatus && (
          <li>
              <Link to="/dashboard" style={{...linkStyle, fontSize: '15px', fontWeight: 'bold'}} className="hover:bg-gray-100 transition-colors">
                  <i className="material-icons" style={iconStyle}>dashboard</i>ダッシュボード
              </Link>
          </li>
      )}

      <li>
        <h2 style={h2Style}><i className="material-icons" style={iconStyle}>description</i>請求書</h2>
        <ul className="pl-2">
            {!isRestrictedStatus && hasPermission('invoice.canUpload') && <li><Link to="/invoice-upload" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>cloud_upload</i>アップロード</Link></li>}
            {!isRestrictedStatus && hasPermission('invoice.canViewScheduled') && <li><Link to="/scheduled-invoices" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>event</i>予約済み請求書</Link></li>}
            {hasPermission('invoice.canViewHistory') && <li><Link to="/billing-history" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>history</i>請求履歴</Link></li>}
        </ul>
      </li>
      
      {!isRestrictedStatus && (customerFeatures.includes(plan)) && (
        <li>
          <h2 style={h2Style}><i className="material-icons" style={iconStyle}>people</i>顧客</h2>
          <ul className="pl-2">
            {hasPermission('customer.canUpload') && <li><Link to="/customer-upload" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>cloud_upload</i>アップロード</Link></li>}
            {hasPermission('customer.canDelete') && <li><Link to="/delete-customer" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>person_remove</i>顧客データ削除</Link></li>}
          </ul>
        </li>
      )}

      {!isRestrictedStatus && isAdmin && teamManagementFeatures.includes(plan) && (
          <li>
          <h2 style={h2Style}><i className="material-icons" style={iconStyle}>corporate_fare</i>組織・メンバー</h2>
          <ul className="pl-2">
              <li><Link to="/settings/team-management" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>group</i>チーム管理</Link></li>
              <li><Link to="/activity" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>history</i>アクティビティ</Link></li>
          </ul>
          </li>
      )}

      <li>
        <h2 style={h2Style}><i className="material-icons" style={iconStyle}>settings</i>設定</h2>
        <ul className="pl-2">
            {hasPermission('settings.canViewSubscription') && <li><Link to="/subscription" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>article</i>ご契約内容</Link></li>}
            {hasPermission('billing.canViewHistory') && <li><Link to="/settings/billing-history" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>request_quote</i>ご利用料金</Link></li>}
            {!isRestrictedStatus && hasPermission('settings.canViewBillwave') && <li><Link to="/settings/billwave" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>tune</i>BillWave設定</Link></li>}
            <li><Link to="/settings/customer-info" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>account_circle</i>お客様情報</Link></li>
        </ul>
      </li>

      <li>
        <h2 style={h2Style}><i className="material-icons" style={iconStyle}>support</i>サポート</h2>
        <ul className="pl-2">
          {!isRestrictedStatus && <li><Link to="/app/support/how-to-use" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>school</i>使い方</Link></li>}
          {!isRestrictedStatus && <li><Link to="/app/support/csv-format" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>description</i>CSVフォーマット</Link></li>}
          {!isRestrictedStatus && <li><Link to="/app/support/faq" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>quiz</i>よくある質問</Link></li>}
          {!isRestrictedStatus && <li><Link to="/app/support/troubleshooting" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>build</i>エラー・トラブル</Link></li>}
          <li><Link to="/app/support/contact" style={linkStyle} className="hover:bg-gray-100 transition-colors"><i className="material-icons" style={subIconStyle}>email</i>お問い合わせ</Link></li>
        </ul>
      </li>
    </ul>
  );

  return (
    <div className="flex flex-col min-h-screen">
      <Header 
        showLogout={true} 
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} 
        isSidebarOpen={isSidebarOpen} 
      />
      
      <div className="flex flex-grow relative overflow-hidden">
        {/* Desktop Sidebar (visible lg and up) */}
        <nav className="hidden lg:block w-60 flex-shrink-0 border-r border-gray-200 p-5 bg-white overflow-y-auto">
          {navContent}
        </nav>

        {/* Mobile Backdrop */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Mobile Off-canvas Sidebar */}
        <nav 
          className={`fixed top-0 left-0 bottom-0 w-64 bg-white z-50 p-5 border-r border-gray-200 overflow-y-auto transform transition-transform duration-300 ease-in-out lg:hidden ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-3">
            <img src="/assets/logo.png" alt="billwave logo" className="h-8" />
            <button 
              onClick={() => setIsSidebarOpen(false)} 
              className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>
          {navContent}
        </nav>

        <main className="flex-1 p-3 sm:p-5 md:p-6 overflow-x-hidden overflow-y-auto bg-gray-50 w-full">
          <Outlet />
        </main>
      </div>
      <Footer />
    </div>
  );
};

const AuthLayout = () => {
  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      <PublicHeader /> 
      <main className="flex-grow flex justify-center items-center p-4 sm:p-6 bg-gray-50">
        <Outlet />
      </main>
      <PublicFooter />
    </div>
  );
};

export { MainLayout, AuthLayout, PublicPageLayout };
